<script setup lang="ts">
import { ImageMedia } from '@clovyr/bed';
import type { Metadata } from '@clovyr/pollen/manifest';
import type { Bundle } from '@clovyr/pollen/types';

import AppBlock from '../blocks/AppBlock.vue';
import ArrowForwardsIcon from '../icons/ArrowForwardsIcon.vue';

const props = defineProps<{ bundle: Bundle; appData: Metadata[] }>();

const collectionLink = computed(() => {
  return `/collection/${props.bundle.id}`;
});

const classNames = computed(() => {
  return [props.bundle.backgroundImage === 'proddev-otters.svg' && 'app-browser__background-otter'];
});
const classNames2 = computed(() => {
  return [props.bundle.id === 'product_and_development' && 'hero-heading__product'];
});

// provide theme variable if available
const colorThemeVar =
  props.bundle.themeColor && `--collection-color-theme: ${props.bundle.themeColor}`;
</script>

<template>
  <div v-if="props.appData.length > 0" class="app-browser" :style="colorThemeVar">
    <div class="app-browser__background" :class="classNames">
      <ImageMedia :filename="bundle.backgroundImage" />
    </div>
    <div class="app-browser-container">
      <div
        class="app-browser__hero-card app-browser__card grid__col-12 grid__col-lg-3"
        :class="classNames"
      >
        <div class="app-browser-hero">
          <div class="app-browser-hero__top">
            <h2 class="hero-heading" :class="classNames2">
              {{ bundle.title }}
            </h2>
            <div class="hero-subheading">
              {{ bundle.tagline }}
            </div>
          </div>
          <router-link :to="collectionLink">
            <div class="app-browser-hero__bottom">
              <div class="collection-link">
                <span class="collection-link__text">
                  all {{ bundle.name }}

                  <ArrowForwardsIcon />
                </span>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <div class="app-browser__content app-browser-grid grid__col-12 grid__col-lg-9">
        <div class="app-browser__card" v-for="appMetadata in props.appData" :key="appMetadata.id">
          <AppBlock :appDatum="appMetadata" :bundle="bundle" />
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/responsive';
@use 'styles/core/transitions';
@use 'styles/core/typography/fonts';
@use 'styles/core/typography/type';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';
@use 'styles/vendor/bootstrap/scss/mixins/grid';

.app-browser {
  position: relative;
  padding: grid-vars.space(6) 0;
  display: flex;
  flex-direction: column;

  .app-browser__content {
    position: relative;
    margin-top: 2rem;

    &.app-browser-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: grid-vars.space(2);

      @include breakpoints.media-breakpoint-up(sm) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include breakpoints.media-breakpoint-up(xl) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      .app-browser__card {
        min-width: 0;
        width: auto;
        max-width: none;
      }
    }

    @include breakpoints.media-breakpoint-up(lg) {
      margin-top: 0;
    }

  }

  &__apps {
    display: flex;
  }

  &__background {
    position: absolute;
    top: 0;
    left: grid-vars.space(13);
    z-index: -1;
  }

  &__background-otter {
    padding-top: grid-vars.space(5);
  }

  &__hero-card {
    @include transitions.transition(padding, slide);

    padding-right: grid-vars.space(1);

    @include breakpoints.media-breakpoint-up(md) {
      margin-top: 3.5rem;
    }

    .app-browser-hero {
      display: flex;
      flex-direction: column;

      @include breakpoints.media-breakpoint-up(lg) {
        height: grid-vars.space(25);
        margin-top: auto;
      }

      .hero-heading {
        @include grid.make-col(12);
        @include fonts.font-size(26);

        @include breakpoints.media-breakpoint-up(md) {
          @include fonts.font-size(30);
        }

        @include breakpoints.media-breakpoint-up(xl) {
          @include fonts.font-size(36);
        }

        color: rgb(var(--collection-color-theme));
      }

      .hero-subheading {
        @include grid.make-col(12);
        @include fonts.font-size(16);

        margin-top: grid-vars.space(1);
        color: colors.color(grey, tertiary);
      }
    }

    .app-browser-hero__bottom {
      margin-top: 1rem;
    }
    .app-browser-hero__top {
      margin-left: 1.5rem;

      @include breakpoints.media-breakpoint-up(sm) {
        margin-left: 0;
      }
    }
  }

  &__top {
    margin-bottom: grid-vars.space(4);

    @include responsive.show-on(tablet-sm-and-below);
  }

  &__bottom {
    margin-top: grid-vars.space(2.5);

    @include responsive.show-on(tablet-sm-and-below);
  }

  .app-browser-container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    @include breakpoints.media-breakpoint-up(md) {
      padding-left: var(--col-gutter-outer-left);
    }
  }

  .collection-link {
    @include type.text--mono;

    text-align: left;
    cursor: pointer;
    color: rgb(var(--collection-color-theme));

    &__text {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      font-size: 14px;
    }

    svg {
      stroke: currentColor;
      margin-left: grid-vars.space(1);
    }
  }

}
</style>
